import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import { usePropertyField, useProperty, useContentToken, EditableToken } from '@vibesites/runtime/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGraduationCap,
  faUtensils,
  faMugHot,
  faDumbbell,
  faBowlingBall,
  faLocationDot,
  faCartShopping,
  faTree,
  faTrain,
  faHeartPulse,
  faFilm,
  faStore,
  faPlus,
  faMinus,
} from '@fortawesome/free-solid-svg-icons';

interface POI {
  poi_name?: string;
  poi_type?: string;
  poi_address?: string;
  poi_short_description?: string;
  poi_long_description?: string;
  poi_featured_image?: string;
  poi_business_url?: string;
  poi_business_phone?: string;
  poi_directions_map_url?: string;
  poi_miles_from_property?: string;
  poi_walking_minutes?: string;
  poi_driving_minutes?: string;
  poi_biking_minutes?: string;
  poi_latitude?: string;
  poi_longitude?: string;
  poi_map_icon?: string;
  poi_google_place_id?: string;
  poi_place_primary_type?: string;
}

// ============================================================================
// Category icons
// ============================================================================

function getCategoryIcon(category: string) {
  const lower = (category || '').toLowerCase();
  if (/school|colleg|educ|univers|academ/.test(lower)) return faGraduationCap;
  if (/coffee|cafe|espresso|bakery/.test(lower)) return faMugHot;
  if (/restaurant|dining|food|eatery|bistro/.test(lower)) return faUtensils;
  if (/gym|fitness|yoga|crossfit|pilates/.test(lower)) return faDumbbell;
  if (/activit|bowling|entertain|leisure|recreation/.test(lower)) return faBowlingBall;
  if (/shop|retail|store|mall|boutique/.test(lower)) return faCartShopping;
  if (/park|nature|trail|garden|outdoor/.test(lower)) return faTree;
  if (/transit|train|station|bus|subway/.test(lower)) return faTrain;
  if (/health|medical|hospital|clinic|pharmac/.test(lower)) return faHeartPulse;
  if (/theater|theatre|cinema|movie|film/.test(lower)) return faFilm;
  if (/grocer|supermarket|market/.test(lower)) return faStore;
  return faLocationDot;
}

// ============================================================================
// Map styling & markers
// ============================================================================

const MAPS_API_KEY =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.GOOGLE_MAPS_API_KEY) || '';

let mapsLoaderPromise: Promise<void> | null = null;

function loadGoogleMaps(): Promise<void> {
  if ((window as any).google?.maps?.Map) return Promise.resolve();
  if (mapsLoaderPromise) return mapsLoaderPromise;
  mapsLoaderPromise = new Promise<void>((resolve, reject) => {
    if (!MAPS_API_KEY) { reject(new Error('No key')); return; }
    const cb = `__gmapsInit_${Date.now()}`;
    (window as any)[cb] = () => { delete (window as any)[cb]; resolve(); };
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_API_KEY}&callback=${cb}`;
    s.onerror = () => { mapsLoaderPromise = null; reject(new Error('Script failed')); };
    document.head.appendChild(s);
  });
  return mapsLoaderPromise;
}

function makeSvgMarker(fillColor: string, glyphSvg: string, size = 30): string {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${Math.round(size * 1.3)}" viewBox="0 0 30 39">
  <path d="M15 0C6.72 0 0 6.72 0 15c0 11.25 15 24 15 24s15-12.75 15-24C30 6.72 23.28 0 15 0z" fill="${fillColor}" stroke="#fff" stroke-width="1.5"/>
  <g transform="translate(7,7)" fill="#fff"><g transform="translate(8,8) scale(0.55) translate(-8,-8)">${glyphSvg}</g></g>
</svg>`)}`;
}

function makeHighlightMarker(fillColor: string, glyphSvg: string, size = 40): string {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${Math.round(size * 1.35)}" viewBox="0 0 40 54">
  <ellipse cx="20" cy="51" rx="8" ry="3" fill="rgba(0,0,0,0.2)"/>
  <path d="M20 0C9 0 0 9 0 20c0 15 20 31 20 31s20-16 20-31C40 9 31 0 20 0z" fill="${fillColor}" stroke="#fff" stroke-width="2"/>
  <g transform="translate(10,10)" fill="#fff"><g transform="translate(10,10) scale(0.7) translate(-8,-8)">${glyphSvg}</g></g>
</svg>`)}`;
}

function makePropertyPinBg(primaryColor: string, hasIcon: boolean): string {
  const inner = hasIcon
    ? `<circle cx="30" cy="27" r="18" fill="#fff"/>`
    : `<circle cx="30" cy="27" r="18" fill="#fff" opacity="0.95"/>
       <path d="M24 26v10h4v-5h4v5h4V26l-6-5z" fill="${primaryColor}"/>
       <path d="M21 27l9-8 9 8" fill="none" stroke="${primaryColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="60" height="74" viewBox="0 0 60 74">
  <ellipse cx="30" cy="70" rx="12" ry="4" fill="rgba(0,0,0,0.2)"/>
  <path d="M30 0C13.43 0 0 13.43 0 30c0 22.5 30 42 30 42s30-19.5 30-42C60 13.43 46.57 0 30 0z" fill="${primaryColor}" stroke="#fff" stroke-width="2.5"/>
  ${inner}
</svg>`)}`;
}

const CATEGORY_GLYPHS: Record<string, string> = {
  restaurant: '<path d="M2 0v6h2v10h2V6h2V0H6V4H4V0zm8 0v8h4V6h2v10h2V0z" transform="scale(0.9)"/>',
  shopping: '<path d="M2 2v2H0v10h16V4h-2V2a4 4 0 00-8 0v0zm2 0a2 2 0 014 0v2H4V2z" transform="scale(0.95)"/>',
  education: '<path d="M8 0L0 4l8 4 8-4-8-4zM2 6v4l6 3 6-3V6L8 9 2 6z"/>',
  fitness: '<path d="M6 0v6H0v4h6v6h4v-6h6V6h-6V0z" transform="scale(0.95)"/>',
  health: '<path d="M6 0v6H0v4h6v6h4v-6h6V6h-6V0z" transform="scale(0.95)"/>',
  entertainment: '<path d="M8 0a8 8 0 100 16A8 8 0 008 0zm0 2a6 6 0 110 12A6 6 0 018 2zm-2 3v6l5-3-5-3z" transform="scale(0.95)"/>',
  park: '<path d="M8 0L4 6h2L2 12h12L10 6h2L8 0zM7 12v4h2v-4z" transform="scale(0.95)"/>',
  transit: '<path d="M4 0C2 0 0 1 0 3v7a3 3 0 003 3l-1 2v1h2l1-2h6l1 2h2v-1l-1-2a3 3 0 003-3V3c0-2-2-3-4-3zm0 2h8v4H4V2zm0 7a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm8 0a1.5 1.5 0 110 3 1.5 1.5 0 010-3z" transform="scale(0.95)"/>',
  grocery: '<path d="M0 0v2h2l3 6.5L3.5 11A2 2 0 005.5 13h9v-2h-9l1-2h6a1 1 0 00.9-.5L15.5 4H4L3 2H0zm5.5 14a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm7 0a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" transform="scale(0.95)"/>',
  default: '<circle cx="8" cy="8" r="4" fill="#fff"/>',
};

function getCategoryGlyph(poiType: string): string {
  const lower = (poiType || '').toLowerCase();
  for (const [key, glyph] of Object.entries(CATEGORY_GLYPHS)) {
    if (key === 'default') continue;
    if (lower.includes(key)) return glyph;
  }
  return CATEGORY_GLYPHS.default;
}

function isColorDark(hex: string): boolean {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  if (!m) return false;
  const r = parseInt(m[1], 16) / 255;
  const g = parseInt(m[2], 16) / 255;
  const b = parseInt(m[3], 16) / 255;
  // Perceived luminance (Rec. 709)
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum < 0.55;
}

// Return black or white — whichever gives readable contrast on the given
// background. Applied consistently to any colored surface (primary panel,
// secondary hover/active state) so text never washes into its background.
function contrastText(hex: string): string {
  return isColorDark(hex) ? '#ffffff' : '#000000';
}

// A subtle, contrast-aware overlay for hairline borders, scrollbars, and
// muted labels that need to sit on a colored surface. Uses the contrast
// color at low opacity so it works on both light and dark backgrounds.
function overlayColor(hex: string, alpha: number): string {
  return isColorDark(hex)
    ? `rgba(255, 255, 255, ${alpha})`
    : `rgba(0, 0, 0, ${alpha})`;
}

function hexToHue(hex: string): number {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return 200;
  const r = parseInt(m[1], 16) / 255, g = parseInt(m[2], 16) / 255, b = parseInt(m[3], 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  if (max === min) return 0;
  const d = max - min;
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return Math.round(h * 360);
}

function hueToHex(hue: number, sat = 45, light = 35): string {
  const h = hue / 360, s = sat / 100, l = light / 100;
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const r = Math.round(hue2rgb(p, q, h + 1 / 3) * 255);
  const g = Math.round(hue2rgb(p, q, h) * 255);
  const b = Math.round(hue2rgb(p, q, h - 1 / 3) * 255);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

function buildMapStyles(primaryColor: string) {
  const hue = hexToHue(primaryColor);
  return [
    { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
    { featureType: 'administrative.land_parcel', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
    { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
    { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
    { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { featureType: 'transit.line', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: hueToHex(hue, 15, 85) }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  ];
}

// ============================================================================
// Google Map component
// ============================================================================

function GoogleMap({
  propertyLat, propertyLng, propertyIcon, primaryColor, pois,
  selectedPoiIndex, onMarkerClick, focusPois,
}: {
  propertyLat: number;
  propertyLng: number;
  propertyIcon: string;
  primaryColor: string;
  pois: POI[];
  selectedPoiIndex: number | null;
  onMarkerClick: (index: number) => void;
  focusPois: POI[];
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<{ marker: any; normalIcon: any; highlightIcon: any; poiIndex: number }[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps().then(() => {
      if (cancelled || !containerRef.current) return;
      const g = (window as any).google.maps;
      const map = new g.Map(containerRef.current, {
        center: { lat: propertyLat, lng: propertyLng },
        zoom: 14,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
        zoomControlOptions: { position: g.ControlPosition.RIGHT_BOTTOM },
        styles: buildMapStyles(primaryColor),
      });

      const hasIcon = !!propertyIcon;
      new g.Marker({
        position: { lat: propertyLat, lng: propertyLng },
        map, title: 'Property', zIndex: 999,
        icon: {
          url: makePropertyPinBg(primaryColor, hasIcon),
          scaledSize: new g.Size(60, 74),
          anchor: new g.Point(30, 74),
        },
      });
      if (hasIcon) {
        new g.Marker({
          position: { lat: propertyLat, lng: propertyLng },
          map, zIndex: 1000, clickable: false,
          icon: {
            url: propertyIcon,
            scaledSize: new g.Size(24, 24),
            anchor: new g.Point(12, 59),
          },
        });
      }
      mapRef.current = map;
      setReady(true);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [propertyLat, propertyLng, primaryColor, propertyIcon]);

  const syncMarkers = useCallback(() => {
    const map = mapRef.current;
    const g = (window as any).google?.maps;
    if (!map || !g) return;

    markersRef.current.forEach((m) => m.marker.setMap(null));
    markersRef.current = [];

    const iconCache = new Map<string, { normal: any; highlight: any }>();

    for (let origIndex = 0; origIndex < pois.length; origIndex++) {
      const poi = pois[origIndex];
      const lat = parseFloat(poi.poi_latitude || '');
      const lng = parseFloat(poi.poi_longitude || '');
      if (isNaN(lat) || isNaN(lng)) continue;

      const pos = { lat, lng };

      const cat = poi.poi_type || 'Nearby';
      if (!iconCache.has(cat)) {
        const glyph = getCategoryGlyph(cat);
        iconCache.set(cat, {
          normal: { url: makeSvgMarker('#222', glyph, 28), scaledSize: new g.Size(28, 36), anchor: new g.Point(14, 36) },
          highlight: { url: makeHighlightMarker(primaryColor, glyph, 40), scaledSize: new g.Size(40, 54), anchor: new g.Point(20, 54) },
        });
      }

      const icons = iconCache.get(cat)!;
      const isSelected = selectedPoiIndex === origIndex;

      const marker = new g.Marker({
        position: pos, map,
        title: poi.poi_name || '',
        icon: isSelected ? icons.highlight : icons.normal,
        zIndex: isSelected ? 900 : 1,
        animation: isSelected ? g.Animation.BOUNCE : null,
      });

      if (isSelected) setTimeout(() => marker.setAnimation(null), 1400);

      marker.addListener('click', () => onMarkerClick(origIndex));

      markersRef.current.push({ marker, normalIcon: icons.normal, highlightIcon: icons.highlight, poiIndex: origIndex });
    }
  }, [pois, primaryColor, selectedPoiIndex, onMarkerClick]);

  useEffect(() => { if (ready) syncMarkers(); }, [ready, syncMarkers]);

  // Fit bounds to the current focus set (property + focusPois). Runs on mount
  // and whenever the caller changes the focus (e.g. expanded category).
  useEffect(() => {
    const g = (window as any).google?.maps;
    const map = mapRef.current;
    if (!g || !map || !ready) return;

    const bounds = new g.LatLngBounds();
    bounds.extend({ lat: propertyLat, lng: propertyLng });
    let hasPoiBounds = false;
    for (const poi of focusPois) {
      const lat = parseFloat(poi.poi_latitude || '');
      const lng = parseFloat(poi.poi_longitude || '');
      if (isNaN(lat) || isNaN(lng)) continue;
      bounds.extend({ lat, lng });
      hasPoiBounds = true;
    }
    if (!hasPoiBounds) return;

    map.fitBounds(bounds, { top: 60, right: 60, bottom: 60, left: 60 });
    const listener = g.event.addListener(map, 'idle', () => {
      if (map.getZoom() > 15) map.setZoom(15);
      g.event.removeListener(listener);
    });
    return () => { g.event.removeListener(listener); };
  }, [focusPois, propertyLat, propertyLng, ready]);

  // When selected POI changes, update marker icons and pan
  useEffect(() => {
    const g = (window as any).google?.maps;
    const map = mapRef.current;
    if (!g || !map || !ready) return;

    for (const entry of markersRef.current) {
      const isSelected = entry.poiIndex === selectedPoiIndex;
      entry.marker.setIcon(isSelected ? entry.highlightIcon : entry.normalIcon);
      entry.marker.setZIndex(isSelected ? 900 : 1);
      if (isSelected) {
        entry.marker.setAnimation(g.Animation.BOUNCE);
        setTimeout(() => entry.marker.setAnimation(null), 1400);
        map.panTo(entry.marker.getPosition());
      }
    }
  }, [selectedPoiIndex, ready]);

  return (
    <div className="w-full h-full relative overflow-hidden">
      <div ref={containerRef} className="w-full h-full" />
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#f5f5f5]">
          <span className="font-body text-sm text-[var(--vibe-color-text-muted)] animate-pulse">Loading map…</span>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function NeighborhoodMap() {
  const property = useProperty();
  const pois = useCollectionBinding<POI>('neighborhoodmap.pois', { collection: 'neighborhood_pois' });
  const heading = useContentToken('neighborhoodmap.heading') as string || 'Explore The Neighborhood';

  const propLat = usePropertyField('property_address_latitude');
  const propLng = usePropertyField('property_address_longitude');
  const propertyIcon = (property?.property_icon as string) || '';
  const primaryColor = (property?.property_primary_color as string) || '#1f3b8b';
  const secondaryColor = (property?.property_secondary_color as string) || '#ece6d4';
  // Pick pure black or white against each colored surface so text stays
  // readable regardless of what the brand colors happen to be.
  const panelTextColor = contrastText(primaryColor);
  const activeTextColor = contrastText(secondaryColor);
  const panelMutedText = overlayColor(primaryColor, 0.75);
  const panelBorder = overlayColor(primaryColor, 0.25);
  const panelScrollThumb = overlayColor(primaryColor, 0.3);

  const allPois = pois && pois.length > 0 ? pois : [];

  const grouped = useMemo(() => {
    const map = new Map<string, { poi: POI; origIndex: number }[]>();
    allPois.forEach((poi, origIndex) => {
      const cat = poi.poi_type || 'Nearby';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push({ poi, origIndex });
    });
    return Array.from(map.entries());
  }, [allPois]);

  const [expandedCat, setExpandedCat] = useState<string | null>(null);
  const [selectedPoiIndex, setSelectedPoiIndex] = useState<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // POIs the map should fit its viewport to. When a category is expanded,
  // zoom to that subset so all of its markers stay visible; otherwise fit
  // everything.
  const focusPois = useMemo(() => {
    if (expandedCat) {
      const found = grouped.find(([cat]) => cat === expandedCat);
      if (found && found[1].length > 0) return found[1].map((entry) => entry.poi);
    }
    return allPois;
  }, [grouped, expandedCat, allPois]);

  // Initialize/refresh expanded category once POIs load
  useEffect(() => {
    if (expandedCat === null && grouped.length > 0) {
      setExpandedCat(grouped[0][0]);
    }
  }, [grouped, expandedCat]);

  const handleToggleCat = useCallback((cat: string) => {
    setExpandedCat((prev) => (prev === cat ? null : cat));
  }, []);

  const handlePoiClick = useCallback((origIndex: number) => {
    setSelectedPoiIndex((prev) => (prev === origIndex ? null : origIndex));
  }, []);

  const handleMarkerClick = useCallback((origIndex: number) => {
    const poi = allPois[origIndex];
    if (!poi) return;
    const cat = poi.poi_type || 'Nearby';
    setExpandedCat(cat);
    setSelectedPoiIndex(origIndex);
    // Scroll the item into view once the accordion has opened
    setTimeout(() => {
      const el = listRef.current?.querySelector(`[data-poi-index="${origIndex}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 60);
  }, [allPois]);

  const lat = parseFloat(propLat as string || '');
  const lng = parseFloat(propLng as string || '');
  const hasCoords = !isNaN(lat) && !isNaN(lng);

  return (
    <section className="nb-section">
      <div className="nb-split">
        <div className="nb-map-side">
          {hasCoords ? (
            <GoogleMap
              propertyLat={lat} propertyLng={lng}
              propertyIcon={propertyIcon} primaryColor={primaryColor}
              pois={allPois}
              focusPois={focusPois}
              selectedPoiIndex={selectedPoiIndex}
              onMarkerClick={handleMarkerClick}
            />
          ) : (
            <div className="w-full h-full bg-[#f5f5f5] flex items-center justify-center text-[var(--vibe-color-text-muted)]">
              <p className="font-body">Map coordinates not available</p>
            </div>
          )}
        </div>

        <div
          className="nb-panel"
          style={{
            background: primaryColor,
            color: panelTextColor,
            ['--nb-primary' as any]: primaryColor,
            ['--nb-secondary' as any]: secondaryColor,
            ['--nb-panel-text' as any]: panelTextColor,
            ['--nb-panel-muted' as any]: panelMutedText,
            ['--nb-panel-border' as any]: panelBorder,
            ['--nb-panel-scroll' as any]: panelScrollThumb,
            ['--nb-active-text' as any]: activeTextColor,
          }}
        >
          <div className="nb-panel-inner">
            <EditableToken
              path="neighborhoodmap.heading"
              as="h2"
              className="nb-panel-heading"
            >
              {heading}
            </EditableToken>

            <div ref={listRef} className="nb-cat-list">
              {grouped.length > 0 ? (
                grouped.map(([cat, items]) => {
                  const isOpen = expandedCat === cat;
                  return (
                    <div key={cat} className={`nb-cat${isOpen ? ' nb-cat--open' : ''}`}>
                      <button
                        type="button"
                        className="nb-cat-header"
                        onClick={() => handleToggleCat(cat)}
                        aria-expanded={isOpen}
                      >
                        <span className="nb-cat-icon">
                          <FontAwesomeIcon icon={getCategoryIcon(cat)} />
                        </span>
                        <span className="nb-cat-label">{cat}</span>
                        <span className="nb-cat-toggle" aria-hidden="true">
                          <FontAwesomeIcon icon={isOpen ? faMinus : faPlus} />
                        </span>
                      </button>
                      {isOpen && (
                        <ul className="nb-cat-items">
                          {items.map(({ poi, origIndex }) => {
                            const isActive = selectedPoiIndex === origIndex;
                            return (
                              <li
                                key={origIndex}
                                data-poi-index={origIndex}
                                className={`nb-cat-item${isActive ? ' nb-cat-item--active' : ''}`}
                                onClick={() => handlePoiClick(origIndex)}
                              >
                                {poi.poi_name || 'Point of interest'}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  );
                })
              ) : (
                <p className="nb-empty">No points of interest available.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .nb-section {
          width: 100%;
          background: var(--vibe-color-background, #fff);
          border-bottom: 1px solid var(--vibe-color-border, rgba(0, 0, 0, 0.15));
        }
        .nb-split {
          display: grid;
          grid-template-columns: 1fr;
          min-height: 420px;
        }
        @media (min-width: 900px) {
          .nb-split {
            grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
            height: 640px;
            min-height: 640px;
          }
        }
        .nb-map-side {
          position: relative;
          min-height: 360px;
          width: 100%;
          overflow: hidden;
        }
        @media (min-width: 900px) {
          .nb-map-side { min-height: 100%; }
        }
        .nb-panel {
          color: var(--nb-panel-text, #fff);
          display: flex;
          align-items: stretch;
          min-height: 360px;
        }
        .nb-panel-inner {
          width: 100%;
          padding: 2.25rem 1.5rem 2rem;
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
          flex: 1;
          min-height: 0;
        }
        @media (min-width: 900px) {
          .nb-panel-inner {
            padding: 3rem 3rem 2.5rem;
          }
        }
        .nb-panel-heading {
          font-family: var(--vibe-font-heading);
          color: var(--nb-panel-text, #fff);
          font-size: clamp(1.75rem, 3.5vw, 2.75rem);
          font-weight: 600;
          line-height: 1.15;
          margin: 0 0 0.5rem;
          letter-spacing: -0.01em;
        }
        .nb-cat-list {
          display: flex;
          flex-direction: column;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: var(--nb-panel-scroll, rgba(255,255,255,0.3)) transparent;
          flex: 1 1 0;
          min-height: 0;
          max-height: 60vh;
        }
        .nb-cat-list::-webkit-scrollbar { width: 4px; }
        .nb-cat-list::-webkit-scrollbar-track { background: transparent; }
        .nb-cat-list::-webkit-scrollbar-thumb { background: var(--nb-panel-scroll, rgba(255,255,255,0.25)); border-radius: 2px; }
        .nb-cat {
          border-bottom: 1px solid var(--nb-panel-border, rgba(255, 255, 255, 0.25));
        }
        .nb-cat:last-child { border-bottom: none; }
        .nb-cat-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          width: 100%;
          padding: 1rem 0;
          background: transparent;
          border: none;
          color: var(--nb-panel-text, #fff);
          font-family: var(--vibe-font-body);
          font-size: 1.05rem;
          font-weight: 600;
          text-align: left;
          cursor: pointer;
          transition: opacity 0.15s ease;
        }
        .nb-cat-header:hover { opacity: 0.85; }
        .nb-cat-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 2rem;
          font-size: 1.25rem;
          color: var(--nb-panel-text, #fff);
          flex-shrink: 0;
        }
        .nb-cat-label {
          flex: 1;
          min-width: 0;
        }
        .nb-cat-toggle {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 1.25rem;
          font-size: 0.9rem;
          color: var(--nb-panel-text, #fff);
          opacity: 0.85;
          flex-shrink: 0;
        }
        .nb-cat-items {
          list-style: none;
          padding: 0 0 0.75rem 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
          animation: nb-fade-in 0.2s ease;
        }
        .nb-cat-item {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--nb-panel-muted, rgba(255, 255, 255, 0.9));
          cursor: pointer;
          /* left padding = icon width (2rem) + header gap (1rem) so item text
             lines up with the category label above it */
          padding: 0.55rem 0.85rem 0.55rem 3rem;
          border-radius: 4px;
          transition: background-color 0.15s ease, color 0.15s ease;
        }
        .nb-cat-item:hover,
        .nb-cat-item--active {
          background: var(--nb-secondary);
          color: var(--nb-active-text);
          font-weight: 600;
        }
        .nb-empty {
          font-family: var(--vibe-font-body);
          color: var(--nb-panel-muted, rgba(255, 255, 255, 0.8));
          font-size: 0.95rem;
        }
        @keyframes nb-fade-in {
          from { opacity: 0; transform: translateY(-2px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .nb-cat-items { animation: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodMap',
  displayName: 'Neighborhood Map',
  tokens: {
    'neighborhoodmap.heading': {
      label: 'Panel Heading',
      type: 'text',
      defaultValue: 'Explore The Neighborhood',
    },
  },
  collectionBindings: {
    'neighborhoodmap.pois': {
      label: 'Points of Interest',
      collection: 'neighborhood_pois',
    },
  },
};

(NeighborhoodMap as any).contentSchema = contentSchema;

export default NeighborhoodMap;
